/**
 * 商品页赠品选择：单选、combo 合计、sequential 互斥档位展示
 */
import { GIFT_CONFIG, selectAvailableGiftId } from 'gift-config';

class GiftProductSelector {
  constructor() {
    this.targetVariantId = 52002627158229;
    this.giftElement = null;
    this.productList = null;
    this.isComboMode = false;
    this.masterVariantPrice = 0;
    this.masterVariantCompare = 0;
    this.comboDiscount = 0;
    this.sequentialApplied = false;
    this.init();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    const shouldShow = this.applyVariantVisibility(this.getMasterVariantId());
    // sequential：同步先隐藏全部卡，避免 T 恤等次档闪一下；再异步露出第一档有货
    this.hideSequentialItemsPending();
    this.applySequentialDisplay().finally(() => {
      if (shouldShow) this.setDefaultSelection();
      this.giftElement?.setAttribute('data-gift-ready', '');
    });
  }

  hideSequentialItemsPending() {
    if (!this.giftElement || !this.productList || this.isComboMode) return;
    const config = GIFT_CONFIG[this.getMasterVariantId()];
    if (!config?.sequential) return;

    this.giftElement.setAttribute('data-sequential-pending', 'true');
    this.productList.querySelectorAll('.product-item').forEach((item) => {
      item.hidden = true;
      item.classList.add('product-item--sequential-hidden');
      item.classList.remove('selected');
    });
  }

  cacheElements() {
    this.giftElement = document.querySelector('.product-page-gift');
    this.productList = document.querySelector(
      '.product-page-gift .product-list',
    );
    this.isComboMode = this.giftElement?.dataset?.giftMode === 'combo';

    if (this.isComboMode && this.giftElement) {
      this.comboDiscount =
        Number(this.giftElement.dataset.comboDiscount) || 0;
      this.masterVariantPrice =
        Number(this.giftElement.dataset.masterVariantPrice) || 0;
      this.masterVariantCompare =
        Number(this.giftElement.dataset.masterVariantCompare) ||
        this.masterVariantPrice;
    }
  }

  getMasterVariantId() {
    const fromData = this.giftElement?.dataset?.masterVariantId;
    if (fromData) return String(fromData);

    const formInput = document.querySelector(
      'form[is="product-form"] [name="id"], product-form [name="id"], form[action*="/cart/add"] [name="id"]',
    );
    if (formInput?.value) return String(formInput.value);

    const urlVariant = new URLSearchParams(window.location.search).get(
      'variant',
    );
    return urlVariant ? String(urlVariant) : '';
  }

  getVisibleVariantIds() {
    const rawIds = this.giftElement?.dataset?.visibleVariantIds?.trim();
    if (!rawIds) return null;

    return new Set(
      rawIds
        .split(/[\s,;]+/)
        .map((id) => id.trim())
        .filter(Boolean),
    );
  }

  applyVariantVisibility(masterVariantId) {
    if (!this.giftElement) return true;

    const visibleVariantIds = this.getVisibleVariantIds();
    const shouldShow =
      visibleVariantIds === null ||
      visibleVariantIds.has(String(masterVariantId || ''));
    this.toggleGiftSection(shouldShow);
    if (!shouldShow) this.clearSelection();
    return shouldShow;
  }

  getItemVariantIds(item) {
    const ids = new Set();
    const primary = item.getAttribute('data-product-id');
    if (primary) ids.add(String(primary));

    const csv = item.getAttribute('data-variant-ids') || '';
    csv
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .forEach((id) => ids.add(String(id)));

    item.querySelectorAll('[data-size-id]').forEach((el) => {
      const sizeId = el.getAttribute('data-size-id');
      if (sizeId) ids.add(String(sizeId));
    });

    return ids;
  }

  findItemForGiftId(giftId) {
    if (!this.productList || !giftId) return null;
    const target = String(giftId);
    const items = Array.from(
      this.productList.querySelectorAll('.product-item'),
    );
    return (
      items.find((item) => this.getItemVariantIds(item).has(target)) || null
    );
  }

  clearSelection() {
    if (!this.productList) return;
    this.productList.querySelectorAll('.product-item.selected').forEach((el) => {
      el.classList.remove('selected');
    });
  }

  selectGiftIdOnItem(item, giftId) {
    if (!item) return;
    const id = String(giftId);
    item.setAttribute('data-product-id', id);

    const sizeOption = item.querySelector(`[data-size-id="${id}"]`);
    if (sizeOption) {
      item.querySelectorAll('.size-selector [data-size-id]').forEach((el) => {
        el.classList.toggle('selected', el === sizeOption);
      });
    }

    this.clearSelection();
    item.classList.add('selected');
  }

  /**
   * sequential：只展示 allowedGiftIds 链上第一个有货档位对应的产品卡。
   * 配置唯一来源 gift-config.js；Liquid 渲染全部卡，此处默认隐藏后再露出。
   */
  async applySequentialDisplay() {
    this.cacheElements();
    if (!this.giftElement || !this.productList || this.isComboMode) {
      return;
    }

    const masterId = this.getMasterVariantId();
    const config = GIFT_CONFIG[masterId];
    if (!config?.sequential) {
      this.sequentialApplied = false;
      this.giftElement.removeAttribute('data-sequential-pending');
      this.productList.querySelectorAll('.product-item').forEach((item) => {
        item.hidden = false;
        item.classList.remove('product-item--sequential-hidden');
      });
      return;
    }

    const allowedGiftIds = (config.allowedGiftIds || []).map(String);
    const availableGiftId = await selectAvailableGiftId(allowedGiftIds);

    const items = Array.from(
      this.productList.querySelectorAll('.product-item'),
    );

    if (!availableGiftId) {
      items.forEach((item) => {
        item.hidden = true;
        item.classList.add('product-item--sequential-hidden');
        item.classList.remove('selected');
      });
      this.giftElement.removeAttribute('data-sequential-pending');
      this.sequentialApplied = true;
      return;
    }

    const activeItem = this.findItemForGiftId(availableGiftId);
    items.forEach((item) => {
      const show = item === activeItem;
      item.hidden = !show;
      item.classList.toggle('product-item--sequential-hidden', !show);
      if (!show) item.classList.remove('selected');
    });

    if (activeItem) {
      this.selectGiftIdOnItem(activeItem, availableGiftId);
    }

    this.giftElement.removeAttribute('data-sequential-pending');
    this.sequentialApplied = true;
  }

  formatMoney(cents = 0) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return '$0.00';
    const sign = cents < 0 ? '-' : '';
    return `${sign}$${(Math.abs(cents) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  updateComboTotal() {
    if (!this.isComboMode || !this.giftElement) return;

    const saleEl = this.giftElement.querySelector('[data-combo-total-sale]');
    const compareEl = this.giftElement.querySelector(
      '[data-combo-total-compare]',
    );
    const saveEl = this.giftElement.querySelector('[data-combo-total-save]');
    if (!saleEl || !compareEl) return;

    const selectedItem = this.productList?.querySelector(
      '.product-item.selected',
    );
    const giftOriginal = selectedItem
      ? Number(selectedItem.dataset.giftVariantPrice) || 0
      : 0;
    const giftSale = giftOriginal
      ? Math.max(0, giftOriginal - this.comboDiscount)
      : 0;

    const totalSale = this.masterVariantPrice + giftSale;
    const totalCompare = this.masterVariantCompare + giftOriginal;

    saleEl.textContent = this.formatMoney(totalSale);
    compareEl.textContent = this.formatMoney(totalCompare);

    const savings = totalCompare - totalSale;

    if (totalCompare > totalSale) {
      compareEl.classList.remove('combo-total-compare--hidden');
    } else {
      compareEl.classList.add('combo-total-compare--hidden');
    }

    if (saveEl) {
      if (savings > 0) {
        saveEl.textContent = `Save ${this.formatMoney(savings)}`;
        saveEl.classList.remove('combo-total-save--hidden');
      } else {
        saveEl.textContent = '';
        saveEl.classList.add('combo-total-save--hidden');
      }
    }
  }

  bindEvents() {
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event);
    });
  }

  async handleVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant) return;

    if (!this.giftElement) {
      this.cacheElements();
    }

    if (this.giftElement && variant.id) {
      this.giftElement.dataset.masterVariantId = String(variant.id);
    }

    const shouldShow = this.applyVariantVisibility(variant.id);

    if (this.isComboMode) {
      this.masterVariantPrice = Number(variant.price) || 0;
      this.masterVariantCompare =
        Number(variant.compare_at_price) || this.masterVariantPrice;
      if (shouldShow) {
        this.updateComboTotal();
      }
      return;
    }

    if (!shouldShow) return;

    await this.applySequentialDisplay();
    this.setDefaultSelection();

    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');

    if (urlVariant && urlVariant !== '52002627158229') {
      // 历史 mini 逻辑保留：特定 variant 时不强制隐藏 sequential 礼品区
    }
  }

  toggleGiftSection(shouldShow) {
    if (this.giftElement) {
      this.giftElement.toggleAttribute('hidden', !shouldShow);
    }
  }

  selectProduct(element, event) {
    if (!element) return;
    if (event?.target?.closest('.product-item-link')) return;

    const productItem = element.classList?.contains('product-item')
      ? element
      : element.closest('.product-item');

    if (productItem?.hidden) return;

    const sizeId = element.getAttribute('data-size-id');
    if (sizeId && productItem) {
      productItem.setAttribute('data-product-id', sizeId);

      const sizeSelector = element.closest('.size-selector');
      if (sizeSelector) {
        Array.from(sizeSelector.children).forEach((option) => {
          option.classList.toggle('selected', option === element);
        });
      }

      this.clearSelection();
      productItem.classList.add('selected');

      event?.stopPropagation();
      return;
    }

    const targetEl = productItem || element;
    if (!targetEl?.parentNode) return;

    if (this.isComboMode && targetEl.classList.contains('product-item')) {
      if (targetEl.classList.contains('selected')) {
        targetEl.classList.remove('selected');
        this.updateComboTotal();
        return;
      }
    }

    this.clearSelection();

    if (targetEl.classList.contains('product-item')) {
      targetEl.classList.add('selected');
    }

    if (this.isComboMode) {
      this.updateComboTotal();
    }
  }

  setDefaultSelection() {
    if (this.isComboMode) {
      this.updateComboTotal();
      return;
    }

    if (!this.productList) return;

    // sequential 已选中可见档时不再改写
    if (this.sequentialApplied) {
      const selected = this.productList.querySelector(
        '.product-item.selected:not([hidden])',
      );
      if (selected) return;
    }

    const selectedItems = Array.from(
      this.productList.querySelectorAll('.product-item.selected'),
    ).filter((item) => !item.hidden);

    if (selectedItems.length > 1) {
      selectedItems.slice(1).forEach((item) => item.classList.remove('selected'));
      return;
    }

    if (selectedItems.length === 1) return;

    const firstVisible = this.productList.querySelector(
      '.product-item:not([hidden])',
    );
    if (firstVisible) {
      firstVisible.classList.add('selected');
    }
  }
}

const giftSelector = new GiftProductSelector();

window.selectGiftProduct = (element, event) => {
  giftSelector.selectProduct(element, event);
};

export { GiftProductSelector };
